import { MEDICAL_SPECIALISATIONS } from './medicalSpecialisations';

export const PACKAGE_SPECIALISATION_STORAGE_KEY = 'thms:ipd:packageCustomSpecialisations';
export const PACKAGE_SURGERY_BY_SPEC_STORAGE_KEY = 'thms:ipd:packageCustomSurgeriesBySpec';

const PLASTIC_AT_TOP = ['Plastic Surgery', 'Plastic & Reconstructive Surgery'] as const;

const PLASTIC_SURGERIES = [
  'Rhinoplasty',
  'Liposuction',
  'VASER Liposuction',
  'Abdominoplasty (Tummy tuck)',
  'Gynecomastia',
  'Breast augmentation',
  'Breast reduction',
  'Breast lift',
  'Body contouring',
  'Facelift',
  'Blepharoplasty',
  'Neck lift',
  'Brow lift',
  'Brachioplasty (Arm lift)',
  'Thigh lift',
  'Brazilian butt lift (BBL)',
  'Mommy makeover',
  'Otoplasty',
  'Chin augmentation',
  'Buccal fat removal',
  'Fat grafting',
  'Scar revision',
  'Laser resurfacing',
  'Hair transplant',
  'Labiaplasty',
  'Other plastic surgery',
] as const;

/** Default surgeries grouped by medical specialisation (IPD package section). */
export const DEFAULT_SURGERIES_BY_SPECIALISATION: Record<string, readonly string[]> = {
  'Plastic Surgery': PLASTIC_SURGERIES,
  'Plastic & Reconstructive Surgery': PLASTIC_SURGERIES,
  'Cosmetic / Aesthetic Medicine': [
    'Botox / fillers',
    'Chemical peel',
    'Laser hair removal',
    'Microneedling',
    'Thread lift',
    'Other aesthetic procedure',
  ],
  'General Surgery': [
    'Appendectomy',
    'Hernia repair',
    'Cholecystectomy',
    'Hemorrhoidectomy',
    'Thyroidectomy',
    'Lipoma excision',
    'Other general surgery',
  ],
  'Gynecology & Obstetrics': [
    'LSCS (C-section)',
    'Normal delivery',
    'Hysterectomy',
    'Myomectomy',
    'Laparoscopic gynec surgery',
    'D&C',
    'Other gynec procedure',
  ],
  'Orthopedics': [
    'Total knee replacement (TKR)',
    'Total hip replacement (THR)',
    'ACL reconstruction',
    'Fracture fixation',
    'Arthroscopy',
    'Spine surgery',
    'Other orthopedic surgery',
  ],
  'Urology': [
    'TURP',
    'PCNL',
    'URS / RIRS',
    'Circumcision',
    'Varicocelectomy',
    'Other urology surgery',
  ],
  'ENT (Otorhinolaryngology)': [
    'Tonsillectomy',
    'Adenoidectomy',
    'Septoplasty',
    'FESS',
    'Tympanoplasty',
    'Other ENT surgery',
  ],
  'Ophthalmology': [
    'Cataract surgery',
    'LASIK / refractive surgery',
    'Glaucoma surgery',
    'Retina surgery',
    'Other eye surgery',
  ],
  'Cardiology': [
    'Angiography',
    'Angioplasty (PTCA)',
    'Pacemaker implant',
    'Other cardiac procedure',
  ],
  'Cardiothoracic Surgery': [
    'CABG',
    'Valve replacement',
    'Thoracic surgery',
    'Other cardiothoracic surgery',
  ],
  'Neurosurgery': [
    'Brain tumor surgery',
    'Spine decompression / fusion',
    'VP shunt',
    'Other neurosurgery',
  ],
  'Gastroenterology': [
    'Endoscopy',
    'Colonoscopy with polypectomy',
    'ERCP',
    'Other GI procedure',
  ],
  'Nephrology': [
    'AV fistula',
    'Renal biopsy',
    'Dialysis catheter',
    'Other nephrology procedure',
  ],
  'Oncology (Surgical)': [
    'Tumor excision',
    'Lymph node dissection',
    'Other oncologic surgery',
  ],
  'Vascular Surgery': [
    'Varicose vein surgery',
    'Bypass surgery',
    'AV access',
    'Other vascular surgery',
  ],
  'Dermatology': [
    'Skin lesion excision',
    'Cyst removal',
    'Other dermatologic procedure',
  ],
  'Pediatrics': [
    'Circumcision',
    'Hernia repair (pediatric)',
    'Other pediatric surgery',
  ],
};

const GENERIC_SURGERIES = ['Procedure / surgery', 'Other'] as const;

export function orderPackageSpecialisations(custom: string[]): string[] {
  const rest = MEDICAL_SPECIALISATIONS.filter(
    (s) => !PLASTIC_AT_TOP.includes(s as (typeof PLASTIC_AT_TOP)[number])
  );
  const extras = custom.filter(
    (c) =>
      !MEDICAL_SPECIALISATIONS.includes(c) &&
      !PLASTIC_AT_TOP.includes(c as (typeof PLASTIC_AT_TOP)[number])
  );
  return [...PLASTIC_AT_TOP, ...rest, ...extras];
}

export function getSurgeriesForSpecialisation(
  specialisation: string,
  customBySpec: Record<string, string[]>
): string[] {
  if (!specialisation.trim()) return [];
  const defaults = DEFAULT_SURGERIES_BY_SPECIALISATION[specialisation] ?? GENERIC_SURGERIES;
  const custom = customBySpec[specialisation] ?? [];
  return Array.from(new Set([...defaults, ...custom])).sort((a, b) =>
    a.localeCompare(b, 'en', { sensitivity: 'base' })
  );
}

/** Infer specialisation from a saved surgery name (for edit mode prefill). */
export function findSpecialisationForSurgery(
  surgeryName: string,
  customBySpec: Record<string, string[]>
): string {
  const nm = surgeryName.trim();
  if (!nm) return '';

  for (const spec of orderPackageSpecialisations([])) {
    if (getSurgeriesForSpecialisation(spec, customBySpec).includes(nm)) {
      return spec;
    }
  }

  if (/rhino|lipo|tummy|gyne|breast|facelift|blepharo|bbl|mommy|otoplasty|abdomino/i.test(nm)) {
    return 'Plastic Surgery';
  }

  return '';
}

export function loadCustomPackageSpecialisations(): string[] {
  try {
    const raw = localStorage.getItem(PACKAGE_SPECIALISATION_STORAGE_KEY) || '[]';
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? parsed.map((x) => String(x || '').trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

export function loadCustomSurgeriesBySpec(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(PACKAGE_SURGERY_BY_SPEC_STORAGE_KEY) || '{}';
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(v)) {
        out[k] = v.map((x) => String(x || '').trim()).filter(Boolean);
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function persistCustomPackageSpecialisations(list: string[]) {
  try {
    localStorage.setItem(PACKAGE_SPECIALISATION_STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

export function persistCustomSurgeriesForSpec(spec: string, surgeries: string[]) {
  try {
    const all = loadCustomSurgeriesBySpec();
    all[spec] = surgeries;
    localStorage.setItem(PACKAGE_SURGERY_BY_SPEC_STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}
